import { useQuery } from '@tanstack/react-query'
import moment from 'moment'

import { scenarioResultsClient } from '@/api'
import { IsoBa, SystemConfigurations } from '@/interfaces'
import { formatters } from '@/utils'

export const useAggregatedSummary = (
  scenarioId: number,
  systemConfiguration: SystemConfigurations,
  isAnnually: boolean,
  projectIsoBa?: IsoBa
) => {
  const QUERY_KEY = [
    ...scenarioResultsClient.QUERY_KEY(scenarioId),
    'aggregatedResults',
    { isAnnually }
  ]

  const { data, isFetching } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      scenarioResultsClient.getAggregatedResults(scenarioId, isAnnually)
  })

  const sections =
    projectIsoBa !== IsoBa.ES_PENINSULA
      ? [
          {
            sectionTitle: 'PV Results',
            sectionItems: [
              {
                label: 'Total PV production (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.totalDcPvProduction)
                )
              },
              {
                label: 'ES charge from PV - clipped (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.esDcChargeFromPvClipped)
                )
              },
              {
                label: 'ES charge from PV - non-clipped (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.esChargeFromPvNonClipped)
                )
              },
              {
                label: 'Inverter clipping (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.inverterClippingDc)
                )
              },
              {
                label: 'Economic curtailment (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.economicCurtailmentDc)
                )
              },
              {
                label: 'Grid export (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.gridExportDc)
                )
              },
              {
                label: 'Grid export (POI)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.gridExportPoi)
                )
              },
              {
                label: 'PV Revenue of grid export',
                values: data?.map(x =>
                  formatters.currency(x.pvResult?.pvRevenueOfGridExport, 'USD')
                )
              },
              {
                label: 'PV revenue loss due to ES operation',
                values: data?.map(x =>
                  formatters.currency(
                    x.pvResult?.pvRevenueLossDueToEsOperation,
                    'USD'
                  )
                )
              },
              {
                label: 'Total PV income - without ES',
                values: data?.map(x =>
                  formatters.currency(x.pvResult?.totalPvIncomeWithoutEs, 'USD')
                )
              }
            ]
          },
          {
            sectionTitle: 'BESS results',
            sectionItems: [
              {
                label: 'Cycle',
                values: data?.map(x => formatters.number(x.bessResults?.cycle))
              },
              {
                label: 'Average SOC',
                values: data?.map(x =>
                  formatters.percentage(x.bessResults?.averageSoc)
                )
              },
              {
                label: 'Total charge (DC)',
                values: data?.map(x =>
                  formatters.number(x.bessResults?.totalChargeDc)
                )
              },
              {
                label: 'Total discharge (DC)',
                values: data?.map(x =>
                  formatters.number(x.bessResults?.totalDischargeDc)
                )
              },
              {
                label: 'Round-trip efficiency (DC)',
                values: data?.map(x =>
                  formatters.percentage(x.bessResults?.roundTripEfficiencyDc)
                )
              },
              {
                label: 'Revenue of DA energy',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfDaEnergy, 'USD')
                )
              },
              {
                label: 'Revenue of RT energy',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfRtEnergy, 'USD')
                )
              },
              {
                label: 'Revenue of Reg up',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfRegUp, 'USD')
                )
              },
              {
                label: 'Revenue of Reg down',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfRegDown, 'USD')
                )
              },
              {
                label: 'Revenue of SR',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfSr, 'USD')
                )
              },
              {
                label: 'PV revenue loss due to ES operation',
                values: data?.map(x =>
                  formatters.currency(
                    x.bessResults?.pvRevenueLossDueToEsOperation,
                    'USD'
                  )
                )
              },
              {
                label: 'Total BESS income',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.totalBessIncome, 'USD')
                )
              }
            ]
          },
          {
            sectionTitle: 'DAM energy',
            sectionItems: [
              {
                label: 'Average LMP price',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.averageLmpPrice, 'USD')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.averageSalePrice, 'USD')
                )
              },
              {
                label: 'Average purchase price',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.averagePurchasePrice, 'USD')
                )
              },
              {
                label: 'Total energy sale',
                values: data?.map(x =>
                  formatters.number(x.damEnergy?.totalEnergySale)
                )
              },
              {
                label: 'Total energy purchase',
                values: data?.map(x =>
                  formatters.number(x.damEnergy?.totalEnergyPurchase)
                )
              },
              {
                label: 'Revenue of energy sale',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.revenueOfEnergySale, 'USD')
                )
              },
              {
                label: 'Cost of energy purchase',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.costOfEnergyPurchase, 'USD')
                )
              }
            ]
          },
          {
            sectionTitle: 'RTM energy',
            sectionItems: [
              {
                label: 'Average LMP price',
                values: data?.map(x =>
                  formatters.currency(x.rtmEnergy?.averageLmpPrice, 'USD')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.rtmEnergy?.averageSalePrice, 'USD')
                )
              },
              {
                label: 'Average purchase price',
                values: data?.map(x =>
                  formatters.currency(x.rtmEnergy?.averagePurchasePrice, 'USD')
                )
              },
              {
                label: 'Total energy sale',
                values: data?.map(x =>
                  formatters.number(x.rtmEnergy?.totalEnergySale)
                )
              },
              {
                label: 'Total energy purchase',
                values: data?.map(x =>
                  formatters.number(x.rtmEnergy?.totalEnergyPurchase)
                )
              },
              {
                label: 'Revenue of energy sale',
                values: data?.map(x =>
                  formatters.currency(x.rtmEnergy?.revenueOfEnergySale, 'USD')
                )
              },
              {
                label: 'Cost of energy purchase',
                values: data?.map(x =>
                  formatters.currency(x.rtmEnergy?.costOfEnergyPurchase, 'USD')
                )
              }
            ]
          },
          {
            sectionTitle: 'Regulation up',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.regulationUp?.averagePrice, 'USD')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.regulationUp?.averageSalePrice, 'USD')
                )
              },
              {
                label: 'Total capacity commitment',
                values: data?.map(x =>
                  formatters.number(x.regulationUp?.totalCapacityCommitment)
                )
              },
              {
                label: 'Total energy deployment',
                values: data?.map(x =>
                  formatters.number(x.regulationUp?.totalEnergyDeployment)
                )
              },
              {
                label: 'Revenue of reg capacity',
                values: data?.map(x =>
                  formatters.currency(
                    x.regulationUp?.revenueOfRegCapacity,
                    'USD'
                  )
                )
              },
              {
                label: 'Revenue of reg energy',
                values: data?.map(x =>
                  formatters.currency(x.regulationUp?.revenueOfRegEnergy, 'USD')
                )
              },
              {
                label: 'Revenue of reg movement',
                values: data?.map(x =>
                  formatters.currency(
                    x.regulationUp?.revenueOfRegMovement,
                    'USD'
                  )
                )
              }
            ]
          },
          {
            sectionTitle: 'Regulation down',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.regulationDown?.averagePrice, 'USD')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.regulationDown?.averageSalePrice, 'USD')
                )
              },
              {
                label: 'Total capacity commitment',
                values: data?.map(x =>
                  formatters.number(x.regulationDown?.totalCapacityCommitment)
                )
              },
              {
                label: 'Total energy deployment',
                values: data?.map(x =>
                  formatters.number(x.regulationDown?.totalEnergyDeployment)
                )
              },
              {
                label: 'Revenue of reg capacity',
                values: data?.map(x =>
                  formatters.currency(
                    x.regulationDown?.revenueOfRegCapacity,
                    'USD'
                  )
                )
              },
              {
                label: 'Revenue of reg energy',
                values: data?.map(x =>
                  formatters.currency(
                    x.regulationDown?.revenueOfRegEnergy,
                    'USD'
                  )
                )
              },
              {
                label: 'Revenue of reg movement',
                values: data?.map(x =>
                  formatters.currency(
                    x.regulationDown?.revenueOfRegMovement,
                    'USD'
                  )
                )
              }
            ]
          },
          {
            sectionTitle: 'Spinning reserve',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.spinningReserve?.averagePrice, 'USD')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(
                    x.spinningReserve?.averageSalePrice,
                    'USD'
                  )
                )
              },
              {
                label: 'Total capacity commitment',
                values: data?.map(x =>
                  formatters.number(x.spinningReserve?.totalCapacityCommitment)
                )
              },
              {
                label: 'Revenue of SR capacity',
                values: data?.map(x =>
                  formatters.currency(
                    x.spinningReserve?.revenueOfSrCapacity,
                    'USD'
                  )
                )
              }
            ]
          }
        ]
      : [
          {
            sectionTitle: 'PV Results',
            sectionItems: [
              {
                label: 'Total PV production (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.totalDcPvProduction)
                )
              },
              {
                label: 'ES charge from PV - clipped (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.esDcChargeFromPvClipped)
                )
              },
              {
                label: 'ES charge from PV - non-clipped (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.esChargeFromPvNonClipped)
                )
              },
              {
                label: 'Inverter clipping (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.inverterClippingDc)
                )
              },
              {
                label: 'Economic curtailment (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.economicCurtailmentDc)
                )
              },
              {
                label: 'Grid export (DC)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.gridExportDc)
                )
              },
              {
                label: 'Grid export (POI)',
                values: data?.map(x =>
                  formatters.number(x.pvResult?.gridExportPoi)
                )
              },
              {
                label: 'PV Revenue of grid export',
                values: data?.map(x =>
                  formatters.currency(x.pvResult?.pvRevenueOfGridExport, 'EUR')
                )
              },
              {
                label: 'PV revenue loss due to ES operation',
                values: data?.map(x =>
                  formatters.currency(
                    x.pvResult?.pvRevenueLossDueToEsOperation,
                    'EUR'
                  )
                )
              },
              {
                label: 'Total PV income - without ES',
                values: data?.map(x =>
                  formatters.currency(x.pvResult?.totalPvIncomeWithoutEs, 'EUR')
                )
              }
            ]
          },
          {
            sectionTitle: 'BESS results',
            sectionItems: [
              {
                label: 'Cycle',
                values: data?.map(x => formatters.number(x.bessResults?.cycle))
              },
              {
                label: 'Average SOC',
                values: data?.map(x =>
                  formatters.percentage(x.bessResults?.averageSoc)
                )
              },
              {
                label: 'Total charge (DC)',
                values: data?.map(x =>
                  formatters.number(x.bessResults?.totalChargeDc)
                )
              },
              {
                label: 'Total discharge (DC)',
                values: data?.map(x =>
                  formatters.number(x.bessResults?.totalDischargeDc)
                )
              },
              {
                label: 'Round-trip efficiency (DC)',
                values: data?.map(x =>
                  formatters.percentage(x.bessResults?.roundTripEfficiencyDc)
                )
              },
              {
                label: 'Revenue of DA energy',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfDaEnergy, 'EUR')
                )
              },
              {
                label: 'Revenue of aFRR: Power',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfAfrrPower, 'EUR')
                )
              },
              {
                label: 'Revenue of aFRR: Energy',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfAFrrEnergy, 'EUR')
                )
              },
              {
                label: 'Revenue of mFRR',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.revenueOfMfrr, 'EUR')
                )
              },
              {
                label: 'PV revenue loss due to ES operation',
                values: data?.map(x =>
                  formatters.currency(
                    x.bessResults?.pvRevenueLossDueToEsOperation,
                    'EUR'
                  )
                )
              },
              {
                label: 'Total BESS income',
                values: data?.map(x =>
                  formatters.currency(x.bessResults?.totalBessIncome, 'EUR')
                )
              }
            ]
          },
          {
            sectionTitle: 'DAM energy',
            sectionItems: [
              {
                label: 'Average LMP price',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.averageLmpPrice, 'EUR')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.averageSalePrice, 'EUR')
                )
              },
              {
                label: 'Average purchase price',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.averagePurchasePrice, 'EUR')
                )
              },
              {
                label: 'Total energy sale',
                values: data?.map(x =>
                  formatters.number(x.damEnergy?.totalEnergySale)
                )
              },
              {
                label: 'Total energy purchase',
                values: data?.map(x =>
                  formatters.number(x.damEnergy?.totalEnergyPurchase)
                )
              },
              {
                label: 'Revenue of energy sale',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.revenueOfEnergySale, 'EUR')
                )
              },
              {
                label: 'Cost of energy purchase',
                values: data?.map(x =>
                  formatters.currency(x.damEnergy?.costOfEnergyPurchase, 'EUR')
                )
              }
            ]
          },
          {
            sectionTitle: 'aFRR: Power Up (secondary reserve)',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.afrrPowerUp?.averagePrice, 'EUR')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.afrrPowerUp?.averageSalePrice, 'EUR')
                )
              },
              {
                label: 'Total capacity commitment',
                values: data?.map(x =>
                  formatters.number(x.afrrPowerUp?.totalCapacityCommitment)
                )
              },
              {
                label: 'Revenue of aFRR: Power Up',
                values: data?.map(x =>
                  formatters.currency(x.afrrPowerUp?.revenueValue, 'EUR')
                )
              }
            ]
          },
          {
            sectionTitle: 'aFRR: Power Down (secondary reserve)',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.afrrPowerDown?.averagePrice, 'EUR')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.afrrPowerDown?.averageSalePrice, 'EUR')
                )
              },
              {
                label: 'Total capacity commitment',
                values: data?.map(x =>
                  formatters.number(x.afrrPowerDown?.totalCapacityCommitment)
                )
              },
              {
                label: 'Revenue of aFRR: Power Down',
                values: data?.map(x =>
                  formatters.currency(x.afrrPowerDown?.revenueValue, 'EUR')
                )
              }
            ]
          },
          {
            sectionTitle: 'aFRR: Energy Up (secondary reserve)',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.afrrEnergyUp?.averagePrice, 'EUR')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.afrrEnergyUp?.averageSalePrice, 'EUR')
                )
              },
              {
                label: 'Average activation ratio',
                values: data?.map(x =>
                  formatters.number(x.afrrEnergyUp?.averageActivationRatio)
                )
              },
              {
                label: 'Total energy deployment',
                values: data?.map(x =>
                  formatters.number(x.afrrEnergyUp?.totalEnergyDeployment)
                )
              },
              {
                label: 'Revenue of aFRR: Energy Up',
                values: data?.map(x =>
                  formatters.currency(x.afrrEnergyUp?.revenueValue, 'EUR')
                )
              }
            ]
          },
          {
            sectionTitle: 'aFRR: Energy Down (secondary reserve)',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.afrrEnergyDown?.averagePrice, 'EUR')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.afrrEnergyDown?.averageSalePrice, 'EUR')
                )
              },
              {
                label: 'Average activation ratio',
                values: data?.map(x =>
                  formatters.number(x.afrrEnergyDown?.averageActivationRatio)
                )
              },
              {
                label: 'Total energy deployment',
                values: data?.map(x =>
                  formatters.number(x.afrrEnergyDown?.totalEnergyDeployment)
                )
              },
              {
                label: 'Revenue of aFRR: Energy Down',
                values: data?.map(x =>
                  formatters.currency(x.afrrEnergyDown?.revenueValue, 'EUR')
                )
              }
            ]
          },
          {
            sectionTitle: 'mFRR: Energy Up (tertiary reserve)',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.mfrrEnergyUp?.averagePrice, 'EUR')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.mfrrEnergyUp?.averageSalePrice, 'EUR')
                )
              },
              {
                label: 'Average activation ratio',
                values: data?.map(x =>
                  formatters.number(x.mfrrEnergyUp?.averageActivationRatio)
                )
              },
              {
                label: 'Total energy deployment',
                values: data?.map(x =>
                  formatters.number(x.mfrrEnergyUp?.totalEnergyDeployment)
                )
              },
              {
                label: 'Revenue of mFRR: Energy Up',
                values: data?.map(x =>
                  formatters.currency(x.mfrrEnergyUp?.revenueValue, 'EUR')
                )
              }
            ]
          },
          {
            sectionTitle: 'mFRR: Energy Down (tertiary reserve)',
            sectionItems: [
              {
                label: 'Average price',
                values: data?.map(x =>
                  formatters.currency(x.mfrrEnergyDown?.averagePrice, 'EUR')
                )
              },
              {
                label: 'Average sale price',
                values: data?.map(x =>
                  formatters.currency(x.mfrrEnergyDown?.averageSalePrice, 'EUR')
                )
              },
              {
                label: 'Average activation ratio',
                values: data?.map(x =>
                  formatters.number(x.mfrrEnergyDown?.averageActivationRatio)
                )
              },
              {
                label: 'Total energy deployment',
                values: data?.map(x =>
                  formatters.number(x.mfrrEnergyDown?.totalEnergyDeployment)
                )
              },
              {
                label: 'Revenue of mFRR: Energy Down',
                values: data?.map(x =>
                  formatters.currency(x.mfrrEnergyDown?.revenueValue, 'EUR')
                )
              }
            ]
          }
        ]

  const dateSection = isAnnually
    ? {
        label: 'Year',
        values: data?.map(x => moment(x.date).format('YYYY'))
      }
    : {
        label: 'Month',
        values: data?.map(x => moment(x.date).format('M/YYYY'))
      }

  const returnedSections = [
    SystemConfigurations.Standalone,
    SystemConfigurations.WindOnShareBus
  ].includes(systemConfiguration)
    ? sections.filter(x => x.sectionTitle !== 'PV Results')
    : sections

  return {
    sections: returnedSections,
    dateSection,
    isFetching,
    areResultsEmpty: !data?.length && !isFetching
  }
}
